import { useCallback, useEffect, useRef, useState, type RefObject } from 'react';
import { useStudio } from '../../context';
import type { DesignEdit__Design } from '../DesignEdit.generated';

export interface StageSize {
  width: number;
  height: number;
  scale: number;
  baseScale: number;
  offsetX: number;
  offsetY: number;
  viewportWidth: number;
  viewportHeight: number;
  canScrollX: boolean;
  canScrollY: boolean;
}

export const useResize = (
  design: DesignEdit__Design | undefined,
  scrollRef: RefObject<HTMLDivElement | null>,
): StageSize => {
  const { stage, zoom } = useStudio();
  const prevZoomRef = useRef(zoom);
  const [size, setSize] = useState<StageSize>({
    width: 1,
    height: 1,
    scale: 1,
    baseScale: 1,
    offsetX: 0,
    offsetY: 0,
    viewportWidth: 1,
    viewportHeight: 1,
    canScrollX: false,
    canScrollY: false,
  });

  const onResize = useCallback(() => {
    if (!design || !stage) return;

    const container = stage.container().parentElement;
    if (!container) return;

    const containerWidth = container.offsetWidth;
    const containerHeight = container.offsetHeight;

    // Calculate base scale to fit design with padding for transform handles
    const padding = 100;
    const availableWidth = containerWidth - padding * 2;
    const availableHeight = containerHeight - padding * 2;

    const scaleX = availableWidth / design.width;
    const scaleY = availableHeight / design.height;
    const baseScale = Math.min(scaleX, scaleY);

    // Apply zoom to the scale
    const scale = baseScale * zoom;

    // Calculate scaled content size with padding
    const scaledWidth = design.width * scale + padding * 2;
    const scaledHeight = design.height * scale + padding * 2;

    // Determine if scrolling is needed
    const canScrollX = scaledWidth > containerWidth;
    const canScrollY = scaledHeight > containerHeight;

    // Stage size: larger of viewport or content
    const stageWidth = canScrollX ? scaledWidth : containerWidth;
    const stageHeight = canScrollY ? scaledHeight : containerHeight;

    // Offset to center design within stage (in unscaled coordinates)
    const offsetX = (stageWidth - design.width * scale) / 2 / scale;
    const offsetY = (stageHeight - design.height * scale) / 2 / scale;

    // Adjust scroll position to keep the viewport center stable
    const scrollEl = scrollRef.current;
    const prevZoom = prevZoomRef.current;
    if (scrollEl && prevZoom !== zoom) {
      const zoomRatio = zoom / prevZoom;

      // Current center point in content pixels
      const centerX = scrollEl.scrollLeft + containerWidth / 2;
      const centerY = scrollEl.scrollTop + containerHeight / 2;

      // Schedule scroll adjustment after DOM updates with new stage size
      requestAnimationFrame(() => {
        scrollEl.scrollLeft = centerX * zoomRatio - containerWidth / 2;
        scrollEl.scrollTop = centerY * zoomRatio - containerHeight / 2;
      });
    }
    prevZoomRef.current = zoom;

    setSize({
      width: stageWidth,
      height: stageHeight,
      scale,
      baseScale,
      offsetX,
      offsetY,
      viewportWidth: containerWidth,
      viewportHeight: containerHeight,
      canScrollX,
      canScrollY,
    });
  }, [design, scrollRef, stage, zoom]);

  useEffect(() => {
    onResize();
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [onResize]);

  return size;
};
